<template>
    <div
        v-if="mainImage"
        ref="containerRef"
        class="relative flex h-full w-full"
        :class="alignmentClasses"
    >
        <component
            :is="linkComponent"
            v-bind="linkProps"
            class="relative w-full"
            :class="{ 'w-full h-full': isFullSize }"
        >
            <MiscImage
                :lazy-load="!isAboveTheFold"
                :loading="!isAboveTheFold"
                :preload="isAboveTheFold ? { fetchPriority: 'high' } : false"
                provider="none"
                :src="mainImage.url"
                :srcset="imageSrcset"
                :alt="getMedia?.alt"
                :title="getMedia?.title"
                fit="fit"
                :style="shouldApplyImageStyle ? imageStyle : {}"
                class="structure-slot-image"
                :class="imageClasses"
            />
        </component>

    </div>
</template>

<script setup lang="ts">
import type {HblSlot} from '@/utils/types'
import {computed, ref} from "vue";
import MiscLink from '../../misc/MiscLink.vue';

const props = defineProps<{
    content: HblSlot
    count?: number
}>()

const isAboveTheFold = computed(() => props.count != null && props.count <= 1)

const getConfig = computed(() => {
    return props?.content?.config
})

const getData = computed(() => {
    return props?.content?.data
})

const getMedia = computed(() => {
    return getData?.value?.media ? getData?.value?.media : getConfig.value?.media
})

const containerRef = ref<HTMLElement | null>(null);
const containerWidth = ref(0);
const dataUrl = ref(getData.value?.url ? getData.value?.url : getConfig.value?.url?.value)
const dataNewTab = ref(getData.value?.newTab ? getData.value?.newTab : getConfig.value?.newTab?.value)
const dataVerticalAlign = ref(getData.value?.verticalAlign ? getData.value?.verticalAlign : getConfig.value?.verticalAlign?.value)
const dataHorizontalAlign = ref(getData.value?.horizontalAlign ? getData.value?.horizontalAlign : getConfig.value?.horizontalAlign?.value)
const dataDisplayMode = ref(getData.value?.displayMode ? getData.value?.displayMode : getConfig.value?.displayMode?.value)
const dataMinHeight = ref(getData.value?.minHeight ? getData.value?.minHeight : getConfig.value?.minHeight?.value)

useResizeObserver(containerRef, (entries) => {
    const entry = entries[0];
    containerWidth.value = entry.contentRect.width;
});

const shouldApplyImageStyle = computed(() => containerWidth.value < 768);

const alignmentClasses = computed(() => {
    const classes: Record<string, boolean> = {};

    const verticalAlign = dataVerticalAlign?.value;
    if (verticalAlign) {
        classes[`items-${verticalAlign}`] = true;
    }

    const horizontalAlign = dataHorizontalAlign?.value;
    if (horizontalAlign) {
        classes[`justify-${horizontalAlign}`] = true;
    }

    return classes;
});

const isFullSize = computed(() =>
    ['cover', 'stretch'].includes(dataDisplayMode?.value ?? '')
);

const imageClasses = computed(() => {
    const displayMode = dataDisplayMode?.value;
    if (displayMode === 'standard') {
        return 'object-contain h-full w-full';
    } else if (displayMode === 'cover') {
        return 'top-0 left-0 object-cover w-full h-full lg:max-h-full';
    } else if (displayMode === 'stretch') {
        return 'object-fill w-full h-full';
    }
    return '';
});

const imageStyle = computed(() => {
    if (dataMinHeight?.value && dataDisplayMode?.value === 'cover') {
        return `min-height: ${props.content.config.minHeight.value};`;
    }
    return '';
});

const linkComponent = computed(() =>
    dataUrl?.value ? MiscLink : 'div'
);

const linkProps = computed(() => ({
    to: dataUrl?.value,
    target: dataNewTab?.value ? '_blank' : undefined
}));

const mainImage = ref(Array.isArray(getMedia.value) ? getMedia.value[0] : getMedia.value);

const imageSrcset = computed(() => {
    if(mainImage?.value?.thumbnails) {
        return mainImage?.value?.thumbnails.map((thumb: { url: any; width: any; }) => `${thumb.url.replace(/\s/g, '%20')} ${thumb.width}w`).join(', ');
    } return false
});

</script>
